import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { AppComponentBase } from '@shared/common/app-component-base';
import { Injector } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { Router } from '@angular/router';

@Injectable()
export class ErpSignalRService extends AppComponentBase {
    private hubConnection: signalR.HubConnection;

    constructor(
        injector: Injector,
        public _zone: NgZone,
        private _router: Router
    ) {
        super(injector);

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(AppConsts.remoteServiceBaseUrl + '/signalr-erp') // SignalR hub URL
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // Retry immediately, then 2s, 10s, 30s - prevents prolonged outage after 1006 disconnects
                    const delays = [0, 2000, 10000, 30000];
                    const index = Math.min(retryContext.previousRetryCount, delays.length - 1);
                    return delays[index];
                }
            })
            .build();
    }

    startConnection() {
        return new Observable<void>((observer) => {
            if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
                this.hubConnection
                    .start()
                    .then(() => {
                        console.log('Connection established with SignalR hub');
                        observer.next();
                        observer.complete();
                    })
                    .catch((error) => {
                        console.error('Error connecting to SignalR hub:', error);
                        observer.error(error);
                    });
            } else {
                // Connected, Reconnecting, or Connecting - complete so subscribers can register handlers
                observer.next();
                observer.complete();
            }
        });
    }

    endConnection() {
        this.hubConnection.off('ProcessPostSync');
        this.hubConnection.off('ProcessCheckSync');
        this.hubConnection.off('ReceiveNotification');
    }

    receiveNotification() {
        this.hubConnection.on('ReceiveNotification', (user, clientId, subdomain, routerLink) => {
            if (this.appSession.tenancyName === subdomain && this._router.url === routerLink && user !== this.appSession.getShownLoginName()) {
                abp.event.trigger("app.handleUnsavedChanges", user, this.appSession.getShownLoginName(), false);
            }
        });
    }

    processPostSync() {
        this.hubConnection.on('ProcessPostSync', (user, clientId, subdomain, routerLink) => {
            console.log(`[SyncLatestData:TRACE] ${new Date().toISOString()} SignalR ProcessPostSync RECEIVED`, { user, clientId, subdomain, routerLink, mySubdomain: this.appSession.tenancyName, myRoute: this._router.url, match: this.appSession.tenancyName === subdomain && this._router.url === routerLink });
            if (this.appSession.tenancyName === subdomain && this._router.url === routerLink) {
                if (user !== this.appSession.getShownLoginName()) {
                    console.log('[SyncLatestData:TRACE] SignalR ProcessPostSync -> triggering app.updateSync (other user synced)');
                    abp.event.trigger("app.updateSync");
                    abp.notify.info(this.l("SyncMade", user));
                } else {
                    abp.notify.info(this.l("SyncingNewData"));
                }
            }
        });
    }

    processCheckSync() {
        this.hubConnection.on('ProcessCheckSync', (user, clientId, subdomain, routerLink, hasChanges) => {
            console.log(`[SyncLatestData:TRACE] ${new Date().toISOString()} SignalR ProcessCheckSync RECEIVED`, { user, clientId, subdomain, routerLink, hasChanges, mySubdomain: this.appSession.tenancyName, myRoute: this._router.url, match: this.appSession.tenancyName === subdomain && this._router.url === routerLink });
            if (this.appSession.tenancyName === subdomain && this._router.url === routerLink) {
                if (user !== this.appSession.getShownLoginName()) {
                    console.log('[SyncLatestData:TRACE] SignalR ProcessCheckSync -> triggering app.disableSync (sync complete)');
                    abp.event.trigger("app.disableSync");
                }

                if (hasChanges) {
                    abp.event.trigger("app.handleUnsavedChanges", user, this.appSession.getShownLoginName(), true);
                }
            }
        });
    }

    notifyUsers(): void {
        this.hubConnection.invoke('NotifyUsers', this.appSession.getShownLoginName(), this.appSession.selectedClientId, this.appSession.tenancyName, this._router.url);
    }

    postSyncCall(): void {
        this.hubConnection.invoke('HandlePostSyncCall', this.appSession.getShownLoginName(), this.appSession.selectedClientId, this.appSession.tenancyName, this._router.url);
    }

    checkSyncCompleteCall(hasChanges: boolean, routerURL: string): void {
        this.hubConnection.invoke('HandleCheckSyncCompleteCall', this.appSession.getShownLoginName(), this.appSession.selectedClientId, this.appSession.tenancyName, routerURL, hasChanges);
    }
}